import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import App from "@/App";
import AppErrorBoundary from "@/components/ui/AppErrorBoundary";
import { ToastViewport } from "@/components/ui/ToastViewport";
import "@/i18n";
import { startDeploymentUpdateCheck } from "@/lib/deploymentUpdate";
import MotionProvider from "@/providers/MotionProvider";
import Web3Provider from "@/providers/Web3Provider";
import "@rainbow-me/rainbowkit/styles.css";
import "@/styles/index.css";

const CHUNK_RELOAD_STORAGE_KEY = "ours-chunk-reload-at";
const CHUNK_RELOAD_COOLDOWN_MS = 60_000;

let chunkReloadRequested = false;

function canReloadAfterChunkError() {
  const now = Date.now();

  try {
    const previousReload = Number(
      window.sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY),
    );

    if (
      Number.isFinite(previousReload) &&
      now - previousReload < CHUNK_RELOAD_COOLDOWN_MS
    ) {
      return false;
    }

    window.sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(now));
    return true;
  } catch {
    const [navigation] = window.performance.getEntriesByType(
      "navigation",
    ) as PerformanceNavigationTiming[];

    return navigation?.type !== "reload";
  }
}

window.addEventListener("vite:preloadError", (event) => {
  if (chunkReloadRequested) {
    event.preventDefault();
    return;
  }

  if (!canReloadAfterChunkError()) return;

  chunkReloadRequested = true;
  event.preventDefault();
  window.location.reload();
});

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Root element #root was not found");

createRoot(rootElement).render(
  <StrictMode>
    <MotionProvider>
      <AppErrorBoundary>
        <Web3Provider>
          <App />
        </Web3Provider>
      </AppErrorBoundary>
      <ToastViewport />
    </MotionProvider>
  </StrictMode>,
);

if (import.meta.env.PROD) startDeploymentUpdateCheck();
